import { useAuth } from "../lib/auth";
import { settings, update, type Theme, type ReaderFont } from "../lib/settings";
import { ExportPanel } from "../components/ExportPanel";
import { ImportPanel } from "../components/ImportPanel";

const THEMES: { key: Theme; label: string }[] = [
  { key: "light", label: "Light" },
  { key: "dark", label: "Dark" },
  { key: "sepia", label: "Sepia" },
];

const FONTS: { key: ReaderFont; label: string }[] = [
  { key: "serif", label: "Serif" },
  { key: "sans", label: "Sans-serif" },
];

export function Settings() {
  const auth = useAuth();
  const s = settings.value;

  return (
    <>
      <div class="topbar"><h1>Settings</h1></div>

      <section class="card" style="margin-bottom: var(--space-5);">
        <h2 style="margin-top: 0;">Appearance</h2>

        <div class="field">
          <label>Theme</label>
          <div class="row">
            {THEMES.map((t) => (
              <button key={t.key} class={s.theme === t.key ? "primary" : ""}
                onClick={() => update({ theme: t.key })}>{t.label}</button>
            ))}
          </div>
        </div>

        <div class="field">
          <label>Reader font</label>
          <div class="row">
            {FONTS.map((f) => (
              <button key={f.key} class={s.readerFont === f.key ? "primary" : ""}
                onClick={() => update({ readerFont: f.key })}>{f.label}</button>
            ))}
          </div>
        </div>

        <div class="field">
          <label for="fs">Font size: {s.readerFontSize}px</label>
          <input id="fs" type="range" min="14" max="24" step="1" value={s.readerFontSize}
            onInput={(e) => update({ readerFontSize: Number((e.target as HTMLInputElement).value) })} />
        </div>

        <div class="field">
          <label for="lh">Line height: {s.readerLineHeight.toFixed(2)}</label>
          <input id="lh" type="range" min="1.3" max="2" step="0.05" value={s.readerLineHeight}
            onInput={(e) => update({ readerLineHeight: Number((e.target as HTMLInputElement).value) })} />
        </div>

        <div class="field">
          <label for="cw">Column width: {s.readerColumn}rem</label>
          <input id="cw" type="range" min="30" max="48" step="1" value={s.readerColumn}
            onInput={(e) => update({ readerColumn: Number((e.target as HTMLInputElement).value) })} />
        </div>
      </section>

      <section class="card" style="margin-bottom: var(--space-5);">
        <h2 style="margin-top: 0;">Account</h2>
        <p class="muted">Signed in as {auth.user?.email}</p>
        <div class="field" style="margin-top: var(--space-4);">
          <label for="token">Access token (for the browser extension)</label>
          <div class="row">
            <input id="token" type="password" readonly value={auth.session?.access_token ?? ""}
              style="font-size: var(--fs-12); font-family: ui-monospace, monospace;" />
            <button class="ghost" onClick={() => {
              const t = auth.session?.access_token ?? "";
              navigator.clipboard.writeText(t).then(() => {
                const el = document.getElementById("token") as HTMLInputElement | null;
                if (el) { el.value = t; el.focus(); el.select(); }
              });
            }}>Copy</button>
          </div>
          <p class="subtle" style="font-size: var(--fs-12);">Paste this into the extension options. It grants access to your account — share it with no one.</p>
        </div>
        <button onClick={() => auth.signOut()}>Sign out</button>
      </section>
      <section class="card" style="margin-bottom: var(--space-5);">
        <h2 style="margin-top: 0;">Your data</h2>
        <p class="muted">Export everything, anytime. Reconstruct your library without this project.</p>
        <ExportPanel />
      </section>
      <section class="card" style="margin-bottom: var(--space-5);">
        <h2 style="margin-top: 0;">Import from Pocket</h2>
        <p class="muted">Migrate a Pocket export. Deduplicates by URL, preserves tags and the oldest save date.</p>
        <ImportPanel />
      </section>

      <section class="card">
        <h2 style="margin-top: 0;">About</h2>
        <p class="muted">
          Stash-text is a text-only Pocket replacement, MIT-licensed and derived from
          Kevin's Stash. No AI, images, feeds, or recommendations. Just reading.
        </p>
      </section>
    </>
  );
}
